/**
 * payoutCycle.ts — quand l'argent se débloque, et ce qui est retirable.
 *
 * Modèle (Kayla, 24-08-2026) :
 *   · une vente se débloque au premier **lundi 12h00 Europe/Paris** qui la suit ;
 *   · une fois débloquée elle le reste — l'employé retire **quand il veut**,
 *     et ce qu'il ne retire pas s'accumule jusqu'au clic ;
 *   · quand il retire, il touche tout le débloqué et repasse à zéro.
 *
 *   à payer    = commission sur les ventes créées AVANT le dernier lundi midi
 *                − déjà versé (clampé ≥ 0).
 *   en attente = commission sur les ventes faites DEPUIS ce lundi midi.
 *
 * ⛔ Le lundi midi DÉBLOQUE, il n'enferme pas. Il n'y a aucune garde sur le
 * jour du retrait : `isWithdrawMonday` a existé quelques heures le 24-08 et a
 * été retirée le jour même.
 *
 * 🪤 CROSS-REF `app/domain/services/payroll/commission.py::derniere_ouverture`.
 * Les deux DOIVENT rendre le même instant. Ce n'est pas un vœu : les tests des
 * deux côtés partagent la MÊME table de cas (`CAS_DE_DEBLOCAGE`), transitions
 * d'heure d'été comprises. Si tu touches à l'une, touche à l'autre.
 *
 * 🩸 Pourquoi tout ce soin : le 23-08 le modèle a changé ici sans changer
 * là-bas. Pendant 24 h le Portefeuille annonçait « $27,08 disponible » pendant
 * que l'API répondait 422. Six chatteurs, $1 074 bloqués, des clics dans le
 * vide. Un commentaire « garder synchronisé » était déjà écrit côté backend.
 *
 * Fonctions PURES → testées en isolation (__tests__/payoutCycle.test.ts).
 */

import {
  calendarDateInZone,
  weekdayInZone,
  zonedDatePlusDays,
  zonedWallTimeToUtc,
} from "../../shared/timezone";

/** Fuseau de référence du déblocage. CROSS-REF `commission.py::PAYOUT_TIMEZONE`. */
export const PAYOUT_TIMEZONE = "Europe/Paris";

/** Heure murale du déblocage hebdomadaire. CROSS-REF `commission.py::UNLOCK_HOUR`. */
export const UNLOCK_HOUR = 12;

export interface PayoutWindow {
  /** Borne basse INCLUSIVE (ISO 8601 Z). */
  from: string;
  /** Borne haute EXCLUSIVE (ISO 8601 Z). */
  to: string;
}

/**
 * Le dernier lundi 12h00 (Europe/Paris) révolu, en epoch ms.
 *
 * 🪤 On travaille sur l'heure MURALE de Paris, puis on localise — jamais en
 * reculant de 7×24 h sur un instant absolu. La semaine d'une bascule d'heure,
 * les deux ne donnent pas le même résultat : le lundi 30-03-2026 à 08h (CEST),
 * la coupure précédente est le 23-03 midi en CET, soit 11h00 UTC et non 10h00.
 */
export function lastUnlockMs(nowMs: number): number {
  // 1 = lundi … 7 = dimanche
  const jour = weekdayInZone(nowMs, PAYOUT_TIMEZONE);
  const lundi = zonedDatePlusDays(nowMs, PAYOUT_TIMEZONE, -(jour - 1));
  const candidat = zonedWallTimeToUtc(
    { ...lundi, hour: UNLOCK_HOUR, minute: 0, second: 0 },
    PAYOUT_TIMEZONE,
  );
  if (candidat <= nowMs) return candidat;

  // Midi n'est pas encore passé ce lundi → la coupure est celle d'avant.
  const lundiPrecedent = zonedDatePlusDays(candidat, PAYOUT_TIMEZONE, -7);
  return zonedWallTimeToUtc(
    { ...lundiPrecedent, hour: UNLOCK_HOUR, minute: 0, second: 0 },
    PAYOUT_TIMEZONE,
  );
}

/**
 * Les DÉBLOCAGES EXCEPTIONNELS : un instant où Kayla décide de payer plus que
 * la semaine close. Ils ne bougent QUE la coupure de PAIE (`coupureDePaieMs`,
 * donc `payableWindow` / `pendingWindow`) — jamais `lastUnlockMs`, que la vue
 * Paie, le classement et le cycle des retraits gardent au lundi 12h00.
 *
 * 🪤 CROSS-REF `commission.py::DEBLOCAGES_EXCEPTIONNELS` — même liste, et la
 * même table de cas (`CAS_DE_COUPURE`) dans les tests des deux dépôts.
 */
export const DEBLOCAGES_EXCEPTIONNELS_MS: readonly number[] = [
  // Kayla, 13-10-2026 : la paie du lundi 12-10 a pris un gros retard, « j'ai
  // décidé de leur donner aussi l'argent de lundi, mardi et aujourd'hui ».
  // Mercredi 14-10-2026 00h00 à Paris.
  Date.parse("2026-10-13T22:00:00Z"),
];

/** La coupure de PAIE, en epoch ms : une vente créée AVANT est payable. Le
 *  dernier lundi 12h00, sauf déblocage exceptionnel passé plus récent.
 *  CROSS-REF `commission.py::coupure_de_paie`. */
export function coupureDePaieMs(nowMs: number): number {
  let coupure = lastUnlockMs(nowMs);
  for (const instant of DEBLOCAGES_EXCEPTIONNELS_MS) {
    if (coupure < instant && instant <= nowMs) coupure = instant;
  }
  return coupure;
}

/** Ventes DÉBLOQUÉES : [epoch, coupure de paie). Base du « à payer ».
 *  `coupureMs` (Portefeuille, 19-10-2026) : la coupure de la dernière semaine
 *  VALIDÉE à la place de celle du lundi — cf `retraitsPrets.mesRetraits`. */
export function payableWindow(nowMs: number, coupureMs = coupureDePaieMs(nowMs)): PayoutWindow {
  return {
    from: new Date(0).toISOString(),
    to: new Date(coupureMs).toISOString(),
  };
}

/** Ventes EN ATTENTE : [coupure de paie, maintenant]. */
export function pendingWindow(nowMs: number, coupureMs = coupureDePaieMs(nowMs)): PayoutWindow {
  return {
    from: new Date(coupureMs).toISOString(),
    to: new Date(nowMs).toISOString(),
  };
}

/** Le prochain lundi 12h00 (Paris), en epoch ms — pour dire à l'employé QUAND
 *  son « en attente » deviendra retirable. */
export function nextUnlockMs(nowMs: number): number {
  const precedent = lastUnlockMs(nowMs);
  const suivant = zonedDatePlusDays(precedent, PAYOUT_TIMEZONE, 7);
  return zonedWallTimeToUtc(
    { ...suivant, hour: UNLOCK_HOUR, minute: 0, second: 0 },
    PAYOUT_TIMEZONE,
  );
}

/** Le jour calendaire (dans le fuseau de paie) du prochain déblocage — pour
 *  l'affichage. */
export function nextUnlockDate(nowMs: number): {
  year: number;
  month: number;
  day: number;
} {
  return calendarDateInZone(nextUnlockMs(nowMs), PAYOUT_TIMEZONE);
}

/**
 * Le prochain déblocage, écrit pour un humain : `{ jour, heure }` lus sur
 * l'horloge de PARIS, quel que soit le fuseau de l'appareil.
 *
 * 🩸 C'est tout l'objet de ce helper. Le 07-09-2026, un chatteur en UTC+3 a
 * ouvert son Portefeuille à 12h15 SUR SON HORLOGE — 11h15 à Paris — a lu
 * « $0,00 disponible » un lundi, et a signalé une panne. Il n'y en avait
 * aucune : il restait 45 minutes. L'écran ne disait ni l'heure du déblocage
 * ni son fuseau, et l'aide affichée disait « clique Retirer le lundi », ce qui
 * lui a fait croire que le jour suffisait.
 *
 * ⛔ Ne jamais formater ce moment dans le fuseau de l'appareil : c'est
 * exactement l'ambiguïté qu'on retire.
 */
export function formatNextUnlock(
  nowMs: number,
  locale: string,
): { jour: string; heure: string } {
  const at = new Date(nextUnlockMs(nowMs));
  const jour = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "short",
    timeZone: PAYOUT_TIMEZONE,
  }).format(at);
  const heure = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: PAYOUT_TIMEZONE,
  }).format(at);
  return { jour, heure };
}
